import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLocationContext } from '../hooks/useLocationContext';
import { useScopedPath } from '../components/ScopedLink';
import {
  useCreateFormMutation,
  useUpdateFormMutation,
  useGetFormQuery,
  useGetFormDistributionsQuery,
  useLazyGetDefaultRegistrationTemplateQuery,
} from '../store/api/localApi';
import {
  FormField,
  FormFieldType,
  FormSection,
  FormType,
  FormStatus,
  FormOwnerLevel,
  isFieldTypeAllowedForFormType,
} from '../types';
import {
  PlusIcon,
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowLeftIcon,
  CheckIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useToast } from '../components/Toast';
import { v4 as uuidv4 } from 'uuid';
import { makeFieldKey } from './formFieldKey';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import { FieldPropertyPanel } from '../components/forms/FieldPropertyPanel';

const fieldTypeLabels: Record<FormFieldType, string> = {
  [FormFieldType.STUDENT_SELECTOR]: 'Student Selector',
  [FormFieldType.TEXT]: 'Text',
  [FormFieldType.TEXTAREA]: 'Text Area',
  [FormFieldType.CHECKBOX]: 'Checkbox',
  [FormFieldType.CHECKBOX_GROUP]: 'Checkbox Group',
  [FormFieldType.RADIO]: 'Radio Buttons',
  [FormFieldType.SELECT]: 'Dropdown',
  [FormFieldType.DATE]: 'Date',
  [FormFieldType.TIME]: 'Time',
  [FormFieldType.NUMBER]: 'Number',
  [FormFieldType.SIGNATURE]: 'Signature',
  [FormFieldType.FILE]: 'File Upload',
  [FormFieldType.CONTACT_LIST]: 'Contact List',
};

const formTypeDescriptions: Record<FormType, string> = {
  [FormType.REGISTRATION]: 'Shown to users after registration',
  [FormType.CONSENT]: 'Consent or permission form sent to parents',
  [FormType.STUDENT_INTAKE]:
    'Optional intake form — staff can send at enrollment or any time later (once per child)',
};

function emptySection(order: number): FormSection {
  return { id: uuidv4(), title: 'New section', order, locked: false, fields: [] };
}

const FormBuilder: React.FC = () => {
  const { showError } = useToast();
  const navigate = useNavigate();
  const { formId } = useParams<{ formId: string }>();
  const isEditing = formId && formId !== 'new';

  const { isOrgLevel } = useAuth();
  const { effectiveLocationId, effectiveOrgId, isInLocation } = useLocationContext();
  const scopedPath = useScopedPath();

  const ownerLevel =
    isOrgLevel && !isInLocation ? FormOwnerLevel.ORGANIZATION : FormOwnerLevel.LOCATION;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [formType, setFormType] = useState<FormType>(FormType.CONSENT);
  const [targetRoles, setTargetRoles] = useState<string[]>(['STAFF']);
  const [sections, setSections] = useState<FormSection[]>([]);
  const [, setStatus] = useState<FormStatus>(FormStatus.DRAFT);

  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  // The "Add field" picker needs to know which section to drop the new field
  // into. When null, the picker buttons are inert.
  const [activeAddSectionId, setActiveAddSectionId] = useState<string | null>(null);

  const [createForm, { isLoading: isCreating }] = useCreateFormMutation();
  const [updateForm, { isLoading: isUpdating }] = useUpdateFormMutation();
  const [fetchDefaultTemplate] = useLazyGetDefaultRegistrationTemplateQuery();
  const { data: existingFormData, isLoading: isLoadingForm } = useGetFormQuery(formId || '', {
    skip: !isEditing,
  });
  const { data: distributions } = useGetFormDistributionsQuery(formId || '', {
    skip: !isEditing,
  });

  const activeDistributions =
    distributions?.filter((d) => d.status === 'ACTIVE' && d.totalInvitations > d.completedCount) ||
    [];
  const hasActiveDistributions = activeDistributions.length > 0;
  const pendingInvitationCount = activeDistributions.reduce(
    (sum, d) => sum + (d.totalInvitations - d.completedCount),
    0
  );

  // Version-bump warning fires when the field tree actually changed; title/
  // description tweaks don't fork (matching the backend gate). JSON deep-eq
  // is cheap at this volume.
  const originalSectionsJson = useMemo(
    () => JSON.stringify(existingFormData?.form?.sections ?? []),
    [existingFormData]
  );
  const currentSectionsJson = useMemo(() => JSON.stringify(sections), [sections]);
  const fieldsChanged = isEditing && originalSectionsJson !== currentSectionsJson;

  const allFields = useMemo(() => sections.flatMap((s) => s.fields), [sections]);

  useEffect(() => {
    if (existingFormData?.form) {
      const form = existingFormData.form;
      setTitle(form.title);
      setDescription(form.description || '');
      setFormType(form.formType);
      setTargetRoles(form.targetRoles);
      setSections(form.sections ?? []);
      setStatus(form.status);
    }
  }, [existingFormData]);

  useEffect(() => {
    if (
      formType === FormType.REGISTRATION ||
      formType === FormType.CONSENT ||
      formType === FormType.STUDENT_INTAKE
    ) {
      setTargetRoles(['PARENT']);
    }
  }, [formType]);

  // When the form type changes, drop any fields whose type isn't compatible.
  // For brand-new forms this also seeds a starting section so the builder
  // isn't a blank screen.
  //
  // A brand-new REGISTRATION form is special: it must carry the fields that map
  // into the child record (the backend rejects one that doesn't — see
  // registration-mapped-fields.ts), so prefill it from the default template
  // rather than a blank section. The admin can then tweak the editable bits
  // (permissions, non-mapped fields); the locked mapped fields stay put.
  useEffect(() => {
    if (!isEditing && formType === FormType.REGISTRATION && effectiveLocationId) {
      let cancelled = false;
      (async () => {
        try {
          const data = await fetchDefaultTemplate({ locationId: effectiveLocationId }).unwrap();
          if (!cancelled) setSections(data.sections);
        } catch {
          // Prefill is best-effort — if it fails, fall back to a blank section
          // so the builder is still usable (the backend guard still applies on
          // save, so an incomplete form can't be activated regardless).
          if (!cancelled) setSections((prev) => (prev.length === 0 ? [emptySection(0)] : prev));
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    setSections((prev) => {
      const filtered = prev.map((s) => ({
        ...s,
        fields: s.fields.filter((f) => isFieldTypeAllowedForFormType(f.type, formType)),
      }));
      if (!isEditing && filtered.length === 0) {
        return [emptySection(0)];
      }
      return filtered;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formType, effectiveLocationId]);

  function addSection() {
    setSections((prev) => [...prev, emptySection(prev.length)]);
  }

  function removeSection(sectionId: string) {
    setSections((prev) =>
      prev.filter((s) => s.id !== sectionId).map((s, idx) => ({ ...s, order: idx }))
    );
  }

  function updateSection(sectionId: string, patch: Partial<FormSection>) {
    setSections((prev) => prev.map((s) => (s.id === sectionId ? { ...s, ...patch } : s)));
  }

  function addField(sectionId: string, type: FormFieldType) {
    const newId = uuidv4();
    setSections((prev) =>
      prev.map((s) =>
        s.id !== sectionId
          ? s
          : {
              ...s,
              fields: [
                ...s.fields,
                {
                  id: newId,
                  // Unique, stable key derived from the field's uuid. The old
                  // `field_${allFields.length + 1}` collided after a delete +
                  // re-add, which made two fields share responses[key] in the
                  // form-fill UI (typing in one filled the other).
                  key: makeFieldKey(newId),
                  type,
                  label: `New ${fieldTypeLabels[type]} Field`,
                  required: false,
                  order: s.fields.length,
                  options: [
                    FormFieldType.RADIO,
                    FormFieldType.SELECT,
                    FormFieldType.CHECKBOX_GROUP,
                  ].includes(type)
                    ? ['Option 1', 'Option 2']
                    : undefined,
                },
              ],
            }
      )
    );
    setEditingFieldId(newId);
  }

  function updateField(sectionId: string, fieldId: string, patch: Partial<FormField>) {
    setSections((prev) =>
      prev.map((s) =>
        s.id !== sectionId
          ? s
          : { ...s, fields: s.fields.map((f) => (f.id === fieldId ? { ...f, ...patch } : f)) }
      )
    );
  }

  function removeField(sectionId: string, fieldId: string) {
    setSections((prev) =>
      prev.map((s) =>
        s.id !== sectionId
          ? s
          : {
              ...s,
              fields: s.fields
                .filter((f) => f.id !== fieldId)
                .map((f, idx) => ({ ...f, order: idx })),
            }
      )
    );
    if (editingFieldId === fieldId) setEditingFieldId(null);
  }

  function moveField(sectionId: string, fieldId: string, dir: 'up' | 'down') {
    setSections((prev) =>
      prev.map((s) => {
        if (s.id !== sectionId) return s;
        const idx = s.fields.findIndex((f) => f.id === fieldId);
        const target = dir === 'up' ? idx - 1 : idx + 1;
        if (idx < 0 || target < 0 || target >= s.fields.length) return s;
        const next = [...s.fields];
        [next[idx], next[target]] = [next[target], next[idx]];
        return { ...s, fields: next.map((f, i) => ({ ...f, order: i })) };
      })
    );
  }

  const handleResetToDefault = async () => {
    if (!effectiveLocationId) {
      showError('Cannot reset: no active location.');
      return;
    }
    const confirmed = window.confirm(
      'Replace the current form with the default registration template? Unsaved changes will be lost.'
    );
    if (!confirmed) return;
    try {
      const data = await fetchDefaultTemplate({ locationId: effectiveLocationId }).unwrap();
      setSections(data.sections);
    } catch (err: any) {
      showError(err?.data?.error || 'Failed to fetch the default registration template.');
    }
  };

  const handleSave = async (saveStatus: FormStatus) => {
    try {
      const formData = {
        title,
        description: description || undefined,
        formType,
        ownerLevel,
        targetRoles,
        sections,
        // Super-admin sessions don't carry an org context on the JWT — the
        // active org is URL-scoped — so the backend can't infer it from
        // claims. Always send it in the body for ORG-owned forms.
        ...(ownerLevel === FormOwnerLevel.ORGANIZATION && effectiveOrgId
          ? { organizationId: effectiveOrgId, locationIds: [] }
          : {}),
        ...(ownerLevel === FormOwnerLevel.LOCATION && effectiveLocationId
          ? { locationId: effectiveLocationId }
          : {}),
      };

      if (isEditing && formId) {
        await updateForm({
          formId,
          updates: { ...formData, status: saveStatus },
        }).unwrap();
      } else {
        await createForm(formData).unwrap();
      }

      navigate(scopedPath('/forms'));
    } catch (error: any) {
      showError(
        error?.data?.error || error?.data?.message || 'Failed to save form. Please try again.'
      );
    }
  };

  if (isLoadingForm) {
    return (
      <div className="py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <p className="mt-2 text-sm text-gray-500">Loading form...</p>
          </div>
        </div>
      </div>
    );
  }

  const hasAnyField = allFields.length > 0;

  return (
    <div className="py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <button
            onClick={() => navigate(scopedPath('/forms'))}
            data-testid="form-builder-back"
            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Forms
          </button>
          <h2 className="mt-4 text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Form' : 'Create New Form'}
          </h2>

          {isEditing && hasActiveDistributions && fieldsChanged && (
            <div
              className="mt-3 rounded-md bg-yellow-50 border border-yellow-200 p-4"
              data-testid="version-conflict-warning"
            >
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Active distributions exist
                  </h3>
                  <p className="mt-1 text-sm text-yellow-700">
                    This form has {activeDistributions.length} active distribution
                    {activeDistributions.length > 1 ? 's' : ''} with {pendingInvitationCount}{' '}
                    outstanding invitation
                    {pendingInvitationCount === 1 ? '' : 's'}. Saving will create version{' '}
                    {(existingFormData?.form?.version ?? 0) + 1}. Existing distributions and any
                    responses parents submit through them will stay on version{' '}
                    {existingFormData?.form?.version}; new distributions you send after this save
                    will use version {(existingFormData?.form?.version ?? 0) + 1}.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Form Details</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Title *</label>
                  <input
                    type="text"
                    value={title}
                    data-testid="form-builder-title"
                    onChange={(e) => setTitle(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    placeholder="Enter form title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={description}
                    data-testid="form-builder-description"
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    placeholder="Describe what this form is for..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Form Type *</label>
                  <select
                    value={formType}
                    data-testid="form-builder-type"
                    onChange={(e) => setFormType(e.target.value as FormType)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  >
                    <option value={FormType.REGISTRATION}>Registration</option>
                    <option value={FormType.CONSENT}>Consent / Permission</option>
                    <option value={FormType.STUDENT_INTAKE}>Student Intake</option>
                  </select>
                  <p className="mt-1 text-sm text-gray-500">{formTypeDescriptions[formType]}</p>
                </div>
              </div>
            </div>

            {sections.length === 0 ? (
              <div className="bg-white shadow rounded-lg p-6">
                <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                  <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">No sections yet</p>
                  <button
                    type="button"
                    onClick={addSection}
                    data-testid="form-builder-add-section"
                    className="mt-3 inline-flex items-center text-sm text-primary-600 hover:text-primary-700"
                  >
                    <PlusIcon className="mr-1 h-4 w-4" />
                    Add a section
                  </button>
                </div>
              </div>
            ) : (
              sections.map((section) => (
                <div
                  key={section.id}
                  data-testid={`form-builder-section-${section.id}`}
                  className="bg-white shadow rounded-lg overflow-hidden"
                >
                  <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                    <div className="flex items-center gap-2 flex-1">
                      {section.locked && (
                        <LockClosedIcon
                          data-testid={`form-builder-section-${section.id}-lock`}
                          className="h-4 w-4 text-gray-400"
                        />
                      )}
                      {section.locked ? (
                        <h3 className="font-medium text-gray-900">{section.title || 'Section'}</h3>
                      ) : (
                        <input
                          type="text"
                          value={section.title}
                          data-testid={`form-builder-section-${section.id}-title`}
                          onChange={(e) => updateSection(section.id, { title: e.target.value })}
                          className="font-medium text-gray-900 bg-transparent border-0 border-b border-transparent focus:border-primary-500 focus:ring-0 flex-1"
                          placeholder="Section title"
                        />
                      )}
                    </div>
                    {!section.locked && (
                      <button
                        type="button"
                        onClick={() => removeSection(section.id)}
                        data-testid={`form-builder-section-${section.id}-remove`}
                        className="text-sm text-red-600 hover:text-red-500"
                      >
                        Remove section
                      </button>
                    )}
                  </div>

                  {section.fields.length === 0 ? (
                    <p className="px-6 py-6 text-sm text-gray-400 italic">No fields yet.</p>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {section.fields.map((field, index) => {
                        const locked = field.locked === true;
                        const requiredLocked = field.requiredLocked === true;
                        return (
                          <div
                            key={field.id}
                            data-testid={`form-field-row-${field.id}`}
                            className={`px-6 py-4 ${
                              editingFieldId === field.id && !locked ? 'bg-primary-50' : ''
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div
                                className={`flex-1 ${locked ? '' : 'cursor-pointer'}`}
                                onClick={() => {
                                  if (!locked)
                                    setEditingFieldId(
                                      editingFieldId === field.id ? null : field.id
                                    );
                                }}
                              >
                                <div className="flex items-center">
                                  <span className="text-xs text-gray-400 mr-2">#{index + 1}</span>
                                  {locked && (
                                    <LockClosedIcon
                                      data-testid={`form-field-row-${field.id}-lock`}
                                      className="h-3.5 w-3.5 mr-1 text-gray-400"
                                      aria-label="Locked field"
                                    />
                                  )}
                                  <span className="font-medium text-gray-900">{field.label}</span>
                                  {field.required && <span className="ml-1 text-red-500">*</span>}
                                </div>
                                <span className="text-xs text-gray-500">
                                  {fieldTypeLabels[field.type]} · key: {field.key}
                                  {locked && (
                                    <span className="ml-2 italic text-gray-400">— locked</span>
                                  )}
                                </span>
                              </div>
                              {!locked && (
                                <div className="flex items-center space-x-1">
                                  <button
                                    onClick={() => moveField(section.id, field.id, 'up')}
                                    disabled={index === 0}
                                    data-testid={`form-field-row-${field.id}-up`}
                                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                  >
                                    <ArrowUpIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => moveField(section.id, field.id, 'down')}
                                    disabled={index === section.fields.length - 1}
                                    data-testid={`form-field-row-${field.id}-down`}
                                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                  >
                                    <ArrowDownIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => removeField(section.id, field.id)}
                                    data-testid={`form-field-row-${field.id}-remove`}
                                    className="p-1 text-red-400 hover:text-red-600"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              )}
                            </div>

                            {editingFieldId === field.id && !locked && (
                              <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Label
                                  </label>
                                  <input
                                    type="text"
                                    value={field.label}
                                    data-testid={`form-field-row-${field.id}-label`}
                                    onChange={(e) =>
                                      updateField(section.id, field.id, { label: e.target.value })
                                    }
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                  />
                                </div>
                                <div className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={field.required}
                                    disabled={requiredLocked}
                                    data-testid={`form-field-row-${field.id}-required`}
                                    onChange={(e) =>
                                      updateField(section.id, field.id, {
                                        required: e.target.checked,
                                      })
                                    }
                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:bg-gray-100"
                                  />
                                  <label className="ml-2 text-sm text-gray-700">
                                    Required field{requiredLocked && ' (locked on)'}
                                  </label>
                                </div>
                                {field.placeholder !== undefined && (
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                      Placeholder
                                    </label>
                                    <input
                                      type="text"
                                      value={field.placeholder || ''}
                                      onChange={(e) =>
                                        updateField(section.id, field.id, {
                                          placeholder: e.target.value,
                                        })
                                      }
                                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                    />
                                  </div>
                                )}
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Help Text
                                  </label>
                                  <input
                                    type="text"
                                    value={field.helpText || ''}
                                    onChange={(e) =>
                                      updateField(section.id, field.id, {
                                        helpText: e.target.value,
                                      })
                                    }
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                    placeholder="Instructions for the user..."
                                  />
                                </div>
                                {field.options !== undefined && (
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                      Options
                                    </label>
                                    <div className="mt-1 space-y-2">
                                      {field.options.map((option, optIndex) => (
                                        <div key={optIndex} className="flex items-center space-x-2">
                                          <input
                                            type="text"
                                            value={option}
                                            onChange={(e) => {
                                              const newOptions = [...(field.options || [])];
                                              newOptions[optIndex] = e.target.value;
                                              updateField(section.id, field.id, {
                                                options: newOptions,
                                              });
                                            }}
                                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                          />
                                          <button
                                            onClick={() => {
                                              const newOptions =
                                                field.options?.filter((_, i) => i !== optIndex) ||
                                                [];
                                              updateField(section.id, field.id, {
                                                options: newOptions,
                                              });
                                            }}
                                            className="text-red-400 hover:text-red-600"
                                          >
                                            <TrashIcon className="h-4 w-4" />
                                          </button>
                                        </div>
                                      ))}
                                      <button
                                        onClick={() => {
                                          const newOptions = [
                                            ...(field.options || []),
                                            `Option ${(field.options?.length || 0) + 1}`,
                                          ];
                                          updateField(section.id, field.id, {
                                            options: newOptions,
                                          });
                                        }}
                                        className="text-sm text-primary-600 hover:text-primary-700"
                                      >
                                        + Add Option
                                      </button>
                                    </div>
                                  </div>
                                )}
                                <FieldPropertyPanel
                                  field={field}
                                  sections={sections}
                                  onChange={(patch) => updateField(section.id, field.id, patch)}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {!section.locked && (
                    <div className="border-t border-gray-100 px-6 py-3">
                      <button
                        type="button"
                        onClick={() =>
                          setActiveAddSectionId((cur) => (cur === section.id ? null : section.id))
                        }
                        data-testid={`form-builder-section-${section.id}-add-field`}
                        className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700"
                      >
                        <PlusIcon className="mr-1 h-4 w-4" />
                        Add field
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}

            {sections.length > 0 && (
              <div>
                <button
                  type="button"
                  onClick={addSection}
                  data-testid="form-builder-add-section"
                  className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700"
                >
                  <PlusIcon className="mr-1 h-4 w-4" />
                  Add section
                </button>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add Fields</h3>
              <p className="text-xs text-gray-500 mb-3">
                {activeAddSectionId
                  ? 'New field will be added to the active section.'
                  : 'Click “Add field” in a section first, then pick a type here.'}
              </p>
              <div className="space-y-2">
                {Object.entries(fieldTypeLabels)
                  .filter(([type]) =>
                    isFieldTypeAllowedForFormType(type as FormFieldType, formType)
                  )
                  .map(([type, label]) => (
                    <button
                      key={type}
                      data-testid={`add-field-${type}`}
                      disabled={!activeAddSectionId}
                      onClick={() => {
                        if (!activeAddSectionId) return;
                        addField(activeAddSectionId, type as FormFieldType);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <PlusIcon className="h-4 w-4 mr-2 text-gray-400" />
                      {label}
                    </button>
                  ))}
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Actions</h3>
              <div className="space-y-3">
                {formType === FormType.REGISTRATION && (
                  <button
                    type="button"
                    onClick={handleResetToDefault}
                    disabled={!effectiveLocationId}
                    data-testid="form-builder-reset-to-default"
                    className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Reset to default
                  </button>
                )}
                <button
                  onClick={() => handleSave(FormStatus.DRAFT)}
                  disabled={!title || isCreating || isUpdating}
                  data-testid="form-builder-save-draft"
                  className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Save as Draft
                </button>
                <button
                  onClick={() => handleSave(FormStatus.ACTIVE)}
                  disabled={!title || !hasAnyField || isCreating || isUpdating}
                  data-testid="form-builder-publish"
                  className="w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                >
                  <CheckIcon className="h-4 w-4 mr-2" />
                  {isCreating || isUpdating ? 'Saving...' : 'Publish Form'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormBuilder;
